import isEqual from 'lodash/isEqual';
import { useState, useCallback, useEffect } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// _mock
import { _roles, USER_ADMIN_OPTIONS, USER_STATUS_OPTIONS, USER_STATUS_OPTIONS_ROLE } from 'src/_mock';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import {
    useTable,
    getComparator, TableNoData, TableHeadCustom,
    TableSelectedAction,
    TablePaginationCustom
} from 'src/components/table';
import UserTableToolbar from './technical-table-toolebar';
import UserTableFiltersResult from './technical-table-filter';
import UserTableRow from './technical-table-row';
import { deleteRequest, getRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { Box } from '@mui/material';
import { LoadingScreen } from 'src/components/loading-screen';
import { applyFilter } from 'src/services/fetching';
//

// ----------------------------------------------------------------------

const STATUS_OPTIONS = [ { value: 'all', label: 'All' }, ...USER_STATUS_OPTIONS ];

const TABLE_HEAD = [
    { id: 'name', label: 'Name' },
    { id: 'role', label: 'User Role', width: 180 },
    // { id: 'phoneNumber', label: 'Registration Date', width: 180 },
    { id: 'status', label: 'Status', width: 100 },
    { id: 'status', label: 'Action', width: 100 },
    { id: '', width: 88 },
];

const defaultFilters = {
    name: '',
    role: [],
    status: 'all',
};

// ----------------------------------------------------------------------

export default function TechnicalUserTable() {
    const table = useTable();

    const settings = useSettingsContext();

    const router = useRouter();

    const confirm = useBoolean();

    const [ tableData, setTableData ] = useState();
    const [ loading, setLoading ] = useState(false);

    const [ filters, setFilters ] = useState(defaultFilters);


    useEffect(() => {
        getUsers();
    }, [ table.page, table.rowsPerPage ]);

    const getUsers = async () => {
        setLoading(true)
        const data = await getRequest(`${endpoints.admin_dashboard.admin_user.user_role.get_list}?role_id=6`);
        if (data) {
            setTableData(data || []);
        }
        setLoading(false)
    };


    const dataFiltered = applyFilter({
        inputData: tableData,
        comparator: getComparator(table.order, table.orderBy),
        filters,
    });

    const dataInPage = dataFiltered?.slice(
        table.page * table.rowsPerPage,
        table.page * table.rowsPerPage + table.rowsPerPage
    );

    const denseHeight = table.dense ? 52 : 72;

    const canReset = !isEqual(defaultFilters, filters);

    const notFound = (!dataFiltered?.length && canReset) || !dataFiltered?.length;

    const handleFilters = useCallback(
        (name, value) => {
            table.onResetPage();
            setFilters((prevState) => ({
                ...prevState,
                [ name ]: value,
            }));
        },
        [ table ]
    );

    const handleDeleteRow = useCallback(
        (id) => {
            const deleteRow = tableData.filter((row) => row.id !== id);
            setTableData(deleteRow);

            table.onUpdatePageDeleteRow(dataInPage.length);
        },
        [ dataInPage?.length, table, tableData ]
    );


    const handleFilterStatus = useCallback(
        (event, newValue) => {
            handleFilters('status', newValue);
        },
        [ handleFilters ]
    );

    const handleResetFilters = useCallback(() => {
        setFilters(defaultFilters);
    }, []);


    const deleteCallBack = () => {
        confirm.onFalse();
        getUsers();
    };
    const handleDeleteRows = async (id) => {
        await deleteRequest(`${endpoints.admin_dashboard.admin_user.delete}/${id}`, deleteCallBack);
    }
    return (
        <>
            { loading ? (
                <Box height={ 700 }>
                    <LoadingScreen />
                </Box>
            ) : (
                <Container maxWidth={ settings.themeStretch ? false : 'xl' }>
                    <CustomBreadcrumbs
                        heading="Technical Manager "
                        links={ [
                            { name: '', href: paths.dashboard.root },

                        ] }
                        // action={
                        //     <Button
                        //         component={ RouterLink }
                        //         href={ paths.dashboard.user.new }
                        //         variant="contained"
                        //         startIcon={ <Iconify icon="mingcute:add-line" /> }
                        //     >
                        //         New User
                        //     </Button>
                        // }
                        sx={ {
                            mb: { xs: 3, md: 5 },
                        } }
                    />

                    <Card>
                        <Tabs
                            value={ filters.status }
                            onChange={ handleFilterStatus }
                            sx={ {
                                px: 2.5,
                                boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[ 500 ], 0.08)}`,
                            } }
                        >
                            { USER_ADMIN_OPTIONS.map((tab) => (
                                <Tab
                                    key={ tab.value }
                                    iconPosition="end"
                                    value={ tab.value }
                                    label={ tab.label }
                                    icon={
                                        <Label
                                            variant={
                                                ((tab.value === 'all' || tab.value === filters.status) && 'filled') || 'soft'
                                            }
                                            color={
                                                (tab.value === 1 && 'success') ||
                                                'default'
                                            }
                                        >
                                            { tab.value === 'all' && dataFiltered?.length }
                                            { tab.value === 1 &&
                                                dataFiltered?.filter((user) => user.status?.value === 1).length }


                                            { tab.value === 2 &&
                                                dataFiltered?.filter((user) => user.status?.value === 2).length }

                                        </Label>
                                    }
                                />
                            )) }
                        </Tabs>

                        <UserTableToolbar
                            filters={ filters }
                            onFilters={ handleFilters }
                            //
                            roleOptions={ _roles }
                        />

                        { canReset && (
                            <UserTableFiltersResult
                                filters={ filters }
                                onFilters={ handleFilters }
                                //
                                onResetFilters={ handleResetFilters }
                                //
                                results={ dataFiltered.length }
                                sx={ { p: 2.5, pt: 0 } }
                            />
                        ) }

                        <TableContainer sx={ { position: 'relative', overflow: 'unset' } }>
                            <TableSelectedAction
                                dense={ table.dense }
                                numSelected={ table.selected.length }
                                rowCount={ tableData?.length }
                                onSelectAllRows={ (checked) =>
                                    table.onSelectAllRows(
                                        checked,
                                        tableData?.map((row) => row.id)
                                    )
                                }
                                action={
                                    <Tooltip title="Delete">
                                        <IconButton color="primary" onClick={ confirm.onTrue }>
                                            <Iconify icon="solar:trash-bin-trash-bold" />
                                        </IconButton>
                                    </Tooltip>
                                }
                            />

                            <Scrollbar>
                                <Table size={ table.dense ? 'small' : 'medium' } sx={ { minWidth: 960 } }>
                                    <TableHeadCustom
                                        order={ table.order }
                                        orderBy={ table.orderBy }
                                        headLabel={ TABLE_HEAD }
                                        rowCount={ tableData?.length }
                                        numSelected={ table.selected.length }
                                        onSort={ table.onSort }
                                        onSelectAllRows={ (checked) =>
                                            table.onSelectAllRows(
                                                checked,
                                                tableData.map((row) => row.id)
                                            )
                                        }
                                    />

                                    <TableBody>
                                        { dataFiltered
                                            ?.slice(
                                                table.page * table.rowsPerPage,
                                                table.page * table.rowsPerPage + table.rowsPerPage
                                            )
                                            .map((row) => (
                                                <UserTableRow
                                                    key={ row.id }
                                                    row={ row }
                                                    selected={ table.selected.includes(row.id) }
                                                    onSelectRow={ () => table.onSelectRow(row.id) }
                                                    onDeleteRow={ () => handleDeleteRows(row.id) }
                                                // onEditRow={ () => handleEditRow(row.id) }
                                                />
                                            )) }

                                        {/* <TableEmptyRows
                                        height={ denseHeight }
                                        emptyRows={ emptyRows(table.page, table.rowsPerPage, tableData.length) }
                                    /> */}

                                        <TableNoData notFound={ notFound } />
                                    </TableBody>
                                </Table>
                            </Scrollbar>
                        </TableContainer>

                        <TablePaginationCustom
                            count={ dataFiltered?.length }
                            page={ table.page }
                            rowsPerPage={ table.rowsPerPage }
                            onPageChange={ table.onChangePage }
                            onRowsPerPageChange={ table.onChangeRowsPerPage }
                            //
                            dense={ table.dense }
                            onChangeDense={ table.onChangeDense }
                        />
                    </Card>
                </Container>
            ) }
            <ConfirmDialog
                open={ confirm.value }
                onClose={ confirm.onFalse }
                title="Delete"
                content={
                    <>
                        Are you sure want to delete <strong> { table.selected.length } </strong> items?
                    </>
                }
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={ () => {
                            // handleDeleteRows();
                            confirm.onFalse();
                        } }
                    >
                        Delete
                    </Button>
                }
            />
        </>
    );
}
